/**
 * Thousand separators for a number a user is still TYPING.
 *
 * Distinct from lib/money.ts and lib/intl.ts, which format a number the app
 * already knows: those take a value and produce a finished string. This one has
 * to survive every halfway state a keyboard passes through — "10.", "-", "0.",
 * "" — and hand back exactly what was typed, only grouped.
 *
 * THE RULE THAT SHAPES EVERYTHING HERE: never round-trip through `Number` or
 * `Decimal`. `Number("10.")` is 10, so reformatting would erase the point the
 * moment it was typed and the decimal would be un-typeable. The integer part is
 * grouped as a STRING, character by character, and the fraction is passed
 * through untouched.
 *
 * Separators come from Intl rather than being hardcoded, so the seam is there if
 * a tenant ever opts into the `arab` numbering system (U+066C group, U+066B
 * decimal). Today every locale this app ships resolves to "," and "." under the
 * `latn` system that lib/intl.ts pins.
 */

import Decimal from 'decimal.js';

import { toAsciiDigits } from './numerals.js';

/** The group and decimal characters for a locale, under a numbering system. */
export interface Separators {
  group: string;
  decimal: string;
}

const SEPARATOR_CACHE = new Map<string, Separators>();

/**
 * Ask Intl what this locale's separators are. Cached — formatToParts is not
 * cheap and this is called on every keystroke.
 */
export function separatorsFor(locale = 'en', numberingSystem: 'latn' | 'arab' = 'latn'): Separators {
  const key = `${locale}/${numberingSystem}`;
  const hit = SEPARATOR_CACHE.get(key);
  if (hit) return hit;
  let sep: Separators = { group: ',', decimal: '.' };
  try {
    const parts = new Intl.NumberFormat(locale, { numberingSystem }).formatToParts(11111.1);
    sep = {
      group: parts.find((p) => p.type === 'group')?.value ?? ',',
      decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
    };
  } catch {
    /* an unknown locale keeps the ASCII default */
  }
  SEPARATOR_CACHE.set(key, sep);
  return sep;
}

/**
 * Strip grouping back out, leaving a raw string safe for the API and for
 * decimal.js. This is the value a form should store and POST — never the
 * displayed one.
 *
 * Deliberately permissive: the result may still be partial ("-", "10.") because
 * that is what the box legitimately holds mid-keystroke. Validation belongs at
 * submit, not at every character.
 */
export function ungroup(display: string, sep: Separators = separatorsFor()): string {
  if (!display) return '';
  // Arabic-Indic (٠١٢٣) and Persian (۰۱۲۳) digits become ASCII FIRST.
  //
  // The loop below keeps only characters in the ASCII range '0'..'9' and lets
  // everything else "fall away" — so on an Arabic keyboard, which is what this
  // shop types on, every digit fell away and the field came back EMPTY. Silently:
  // the box just refused to hold a number. Every price input in the app is built
  // on this function.
  //
  // Not normalizeNumericInput(): that strips the ASCII comma as a thousands
  // separator, which would be wrong for a locale whose DECIMAL separator is a
  // comma — the `sep` argument exists precisely to decide that per-locale.
  let out = '';
  for (const ch of toAsciiDigits(display)) {
    if (ch >= '0' && ch <= '9') out += ch;
    else if (ch === '-' && out.length === 0) out += '-';
    // U+066B is the Arabic decimal separator — a decimal point by any other name.
    else if (ch === sep.decimal || ch === '.' || ch === '٫') {
      // Only the first one is a decimal point; a second is a typo, not a number.
      if (!out.includes('.')) out += '.';
    }
    // group separators and anything else fall away
  }
  return out;
}

/** Insert separators into a run of digits, right to left. */
function groupDigits(digits: string, group: string): string {
  if (digits.length <= 3) return digits;
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    // A separator goes before every digit whose distance from the end is a
    // multiple of three — except the very first character.
    if (i > 0 && (digits.length - i) % 3 === 0) out += group;
    out += digits[i];
  }
  return out;
}

/**
 * Raw → what the box should show.
 *
 * Preserves the halfway states rather than "correcting" them: a trailing decimal
 * point stays, a lone "-" stays, and the fraction is never padded or rounded
 * while the caret is still in the field. Rounding to the currency's decimals is
 * a BLUR-time concern, not a keystroke-time one — doing it here would fight the
 * person typing "1.05" the moment they reached "1.0".
 */
export function group(raw: string, sep: Separators = separatorsFor()): string {
  if (!raw) return '';
  const neg = raw.startsWith('-');
  const body = neg ? raw.slice(1) : raw;
  // Split on the FIRST point only; anything after a second is not a number.
  const dot = body.indexOf('.');
  const intPart = dot === -1 ? body : body.slice(0, dot);
  const fracPart = dot === -1 ? null : body.slice(dot + 1).replace(/\D/g, '');
  const digits = intPart.replace(/\D/g, '');

  // "-" and ".5" are real things to have typed; neither has an integer part.
  const grouped = groupDigits(digits, sep.group);
  let out = (neg ? '-' : '') + grouped;
  if (dot !== -1) out += sep.decimal + fracPart;
  return out;
}

/**
 * How many digits sit to the left of `caret` in `display`.
 *
 * This is the whole trick to keeping the caret still. Character offsets shift
 * when separators appear or vanish, but the DIGIT the caret sits after does not
 * — so we count digits, reformat, then walk forward to the same digit count.
 */
export function digitsBefore(display: string, caret: number): number {
  let n = 0;
  for (let i = 0; i < caret && i < display.length; i++) {
    const ch = display[i] ?? '';
    if ((ch >= '0' && ch <= '9') || ch === '.' || ch === '-') n++;
  }
  return n;
}

/** The character offset in `display` that sits after `count` digits. */
export function caretAfterDigits(display: string, count: number): number {
  if (count <= 0) return 0;
  let n = 0;
  for (let i = 0; i < display.length; i++) {
    const ch = display[i] ?? '';
    if ((ch >= '0' && ch <= '9') || ch === '.' || ch === '-') n++;
    if (n >= count) return i + 1;
  }
  return display.length;
}

/**
 * Round a raw value to a currency's decimals. For BLUR, not for typing.
 *
 * Leaves partial input alone: "" and "-" are states to leave be, not to turn
 * into "0.00" under someone's cursor.
 */
/**
 * Keep up to `max` decimals: round only what goes past them, never pad, and tidy
 * a bare point — ".7333" → "0.7333", "5." → "5", "0.21215" → "0.2122", "1.5"
 * stays "1.5". For a unit price, where a currency's cents are too coarse (a pen
 * bought at $0.0278).
 */
export function capRaw(raw: string, max: number): string {
  if (raw === '' || raw === '-' || raw === '.') return raw;
  let r = raw.endsWith('.') ? raw.slice(0, -1) : raw;
  if (r.startsWith('-.')) r = `-0${r.slice(1)}`;
  else if (r.startsWith('.')) r = `0${r}`;
  const dot = r.indexOf('.');
  if (dot === -1 || r.length - dot - 1 <= max) return r;
  try {
    return new Decimal(r).toDecimalPlaces(max, Decimal.ROUND_HALF_UP).toFixed();
  } catch {
    return raw;
  }
}

export function roundRaw(raw: string, decimals: number | undefined): string {
  if (raw === '' || raw === '-' || raw === '.' || decimals === undefined) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(decimals);
}
